import React from 'react';
import { NavLink, useParams, generatePath, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../hooks/layout';
import { useGetRecipeQuery, useGetRelatedRecipesQuery } from '../../services/recipes';
import '../../translations/i18n';
import { setImagevariant, readableTime } from '../../utils/helpers';
import { translationsMaps } from '../../utils/maps';
import Layout from '../../shared/Layout';
import Media from '../../shared/Media';
import Img from '../../shared/Img';
import Markdown from '../../shared/Markdown';
import Slider from '../../shared/Slider';
import Accordion from '../../shared/Accordion';
import { Highlights, Rating, Share, Bookmark, Chat, Card } from '../../shared/Recipe';
import CategoriesBreadCrumbs from '../../shared/CategoriesBreadCrumbs';
import { Grid, Typography, Box, Button, ButtonBase, Avatar, Stack, Paper, Chip } from '@mui/material';
import { DishIcon, ClockIcon, PiggyIcon, PotIcon } from '../../shared/Icons';

const Related = (props) => {

	const { id } = props;
	const { isMobile } = useLayout();
	const [formParams, setFormParams] = React.useState({
		page_index: 1,
		page_size: 20	
	});
	const [recipes, setRecipes] = React.useState([]);
	const { data, currentData, isFetching, isSuccess } = useGetRelatedRecipesQuery({id: id, body: formParams});

	React.useEffect(() => {
		if(isSuccess && !isFetching) {
			setRecipes((oldRecipes) => {
				const newRecipes = [...oldRecipes];
				return newRecipes.concat(data.items);
			});
		}
	}, [isSuccess, data]);

	React.useLayoutEffect(() => {
		setRecipes([]);
	}, [id]);

	return(
		<Slider
			dontScroll={isMobile}
			onScrollEnd={() => {
				if(!isFetching) {
					setFormParams({
						...formParams,
						page_index: formParams.page_index + 1
					});
				}
			}} 
		>
			{ 
				Boolean(recipes.length) &&  
				recipes.map((recipe) => (
					<Box
						key={recipe.id}
						sx={{
							width: {
								xs: '100%',
								md: 'auto'
							},
							mb: {
								xs: 2,
								md: 0
							}
						}}
					>
						<Card 
							responsive
							recipe={recipe}
						/>
					</Box>
				))
			}
			<Box
				sx={{
					width: {
						xs: '100%',
						md: 'auto'
					}
				}}
			>
				<Card responsive />
			</Box>
		</Slider>
	);
}

const Recipe = () => {

	const { t } = useTranslation();
	const navigate = useNavigate();
	const { slug } = useParams();
	const {isMobile, theme} = useLayout();

	const [hasRated, setHasRated] = React.useState(false);

	const { data, isFetching, refetch, isSuccess, isError } = useGetRecipeQuery(slug);

	React.useEffect(() => {
		if(hasRated) {
			setTimeout(async () => {
				await refetch();
				setHasRated(false)
			}, 2000);
		}
	}, [hasRated]);

	React.useEffect(() => {
		if(isError) {
			navigate('/404')
		}
	}, [isError]);

	return(
		<Layout 
			seo={data ? data.seo : {}}
			loading={isFetching}
		>	
			{
				isSuccess &&
				<>
					<Box
						sx={{
							display: {
								md: 'none'
							},
							ml: -2,
							mr: -2,
							borderRadius: 3,
							overflow: 'hidden',
							pt: 9
						}}
					>
						<Media 
							autoplay
							cover={data.cover}
							video={data.video}
						/>
						<Highlights 
							difficulty={data.difficulty}
							duration={data.times.total}
							rating={data.rating}
							hasRated={hasRated}
						/>
					</Box>
					<Grid 
						container 
						justifyContent="center" 
						sx={{
							pt: {
								xs: 3,
								md: 17
							},
							pb: 6
						}}
					>
						<Grid 
							item 
							xs={12} 
							md={11} 
							xl={10} 
						>
							{
								!data.rating.user_rated &&
								<Paper
									sx={{
										mb: 3,
										p: 1,
										pl: 2,
										display: {
											xs: 'block',
											md: 'none'
										}
									}}
								>
									<Stack
										direction="row"
										alignItems="center"
										justifyContent="space-between"
									>
										<Typography
											color="yellow.main"
											variant="caption"
										>
											<b>{t("recipeDetails_rating_header")}</b>
										</Typography>
										<Rating 
											id={data.id}
											rating={data.rating} 
											onNewValue={(value) => setHasRated(true) }
										/>
									</Stack>
								</Paper>
							}
							<Stack
								direction="row"
								justifyContent="space-between"
								alignItems="center"
							>
								<CategoriesBreadCrumbs categories={data.categories} />
								<Stack
									direction="row"
									sx={{
										display: {
											md: 'none'
										}
									}}
								>
									<Share url={data.share_url} />
									<Bookmark id={data.id} saved={data.user_favorite} refetch={refetch} />
								</Stack>
							</Stack>				
							<Typography variant="h1" color="textPrimary" sx={{my:1, lineHeight: 1.1}}><b>{data.title}</b></Typography>
							{
								Boolean(data.chef) &&
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="center"
								>
									<Box>
										<ButtonBase
											component={NavLink}
											to={generatePath('/chef/:slug', {slug: data.chef.slug})}
										>
											<Avatar 
												src={setImagevariant(data.chef.image.url, 100)} 
												sx={{mr: 2}}
											/>
											<Typography color="textPrimary" component="div">
												<b>{data.chef.name}</b>
											</Typography>
										</ButtonBase>
									</Box>
									<Box
										sx={{
											display: {
												md: 'none'
											}
										}}
									>
										<Chat link={data.chat_url} />
									</Box>
								</Stack>
							}
						</Grid>
						<Grid item xs={12} md={11} xl={10} sx={{mt: 3}}>
							<Grid 
								container 
								justifyContent="center" 
								direction={isMobile ? "column-reverse" : "row"}
								spacing={4}
							>
								<Grid item xs={12} md={6}>
									<Box
										sx={{
											display: {
												xs: 'none',
												md: 'block'
											}
										}}
									>
										<Media 
											autoplay
											cover={data.cover}
											video={data.video}
											containerRadius={3}
										/>
										<Stack
											direction="row"
											justifyContent="space-between"
										>
											<Stack
												direction="row"
											>
												<Share url={data.share_url} />
												<Bookmark id={data.id} saved={data.user_favorite} refetch={refetch} />
											</Stack>
											{ 
												Boolean(data.chat_url) &&
												<Box>
													<Chat link={data.chat_url} />
												</Box>
											}
										</Stack>
									</Box>
									<Box mt={3}>
										<Chip component="h3" label={t("recipeDetails_steps_header")} color="primary" />
										{
											data.steps.map((step, i) => (
												<Box key={'step-'+i} id={'step-'+i} mt={3}>
													<Typography
														component="div"
														variant="body2"
														color="textPrimary"
													>
														<Box
															sx={{
																display: 'inline-block',
																background: theme.palette.yellow.main,
																width: '25px',
																pt: 0.4,
																pb: 0.3,
																textAlign: 'center',
																borderRadius: 2,
																color: 'white !important',
																fontSize: '0.9rem',
																verticalAlign: 'top',
																float: 'left',
																mr: 1
															}}
														>
															<b>{(i+1)}</b>
														</Box> <Typography component={Markdown} sx={{'&::after': {clear: 'both', content: '""'}}}>{step.body}</Typography>
													</Typography>
													{
														Boolean(step.images.length) &&
														<Box  mt={2}>
															<Grid container spacing={1}>
																{
																	step.images.map((image) => (
																		<Grid key={image.id} item xs>
																			<Img 
																				variant 
																				skeleton
																				image={image}
																				alt={data.title}
																				skeletonProps={{
																					variant: 'rectangular',
																					sx: {
																						width: '100%', 
																						paddingTop: '47.36%'
																					}
																				}}
																				sx={{
																					width: '100%',
																					borderRadius: '8px'
																				}} 
																			/>
																		</Grid>
																	))
																}		
															</Grid>
														</Box>
													}
												</Box>
											))
										}
									</Box>
								</Grid>
								<Grid item xs={12} md={6} sx={isMobile ? {maxWidth: '100% !important'} : {}}>
									<Paper elevation={0}>
										<Box
											sx={{
												p: {
													xs: 1,
													md: 3
												}
											}}
										>
											<Grid 
												container 
												spacing={{
													xs: 1,
													md: 2
												}}
											>
												{
													Boolean(data.diet) &&
													<Grid item xs={6}>
														<Stack
															direction="row"
															alignItems="center"
														>
															<Avatar 
																sx={{
																	background: '#F5F1EA',
																	width: '3.2rem',
																	height: '3.2rem'
																}} 
																variant="rounded"
															>
																<DishIcon fontSize="large" />
															</Avatar>
															<Box
																sx={{
																	ml: 2
																}}
															>
																<ButtonBase
																	component={NavLink}
																	to={generatePath('/diete/:slugDiet', {slugDiet: data.diet.slug})}
																>
																	<Typography 
																		color="textPrimary"
																		sx={{
																			lineHeight: 1
																		}}
																	>
																		<Typography 
																			variant="body2" 
																			color="textSecondary"
																			component="span"
																		>
																			{t("recipeDetails_info_dietHeader")}
																		</Typography><br/>
																		<b>{data.diet.name}</b>
																	</Typography>
																</ButtonBase>
															</Box>
														</Stack>
													</Grid>
												}
												{
													Boolean(data.times.preparing) &&
													<Grid item xs={6}>
														<Stack
															direction="row"
															alignItems="center"
														>
															<Avatar 
																sx={{
																	background: '#F5F1EA',
																	width: '3.2rem',
																	height: '3.2rem'
																}} 
																variant="rounded"
															>
																<ClockIcon fontSize="large" />
															</Avatar>
															<Box
																sx={{
																	ml: 2
																}}
															>
																<Typography 
																	color="textPrimary"
																	sx={{
																		lineHeight: 1
																	}}
																>
																	<Typography 
																		variant="body2" 
																		color="textSecondary"
																		component="span"
																	>
																		{t("recipeDetails_info_preparingTimeHeader")}
																	</Typography><br/>
																	<b>{readableTime(data.times.preparing, 'minutes')}</b>
																</Typography>
															</Box>
														</Stack>
													</Grid>
												}
												{
													Boolean(data.cost) &&
													<Grid item xs={6}>
														<Stack
															direction="row"
															alignItems="center"
														>
															<Avatar 
																sx={{
																	background: '#F5F1EA',
																	width: '3.2rem',
																	height: '3.2rem'
																}} 
																variant="rounded"
															>
																<PiggyIcon fontSize="large" />
															</Avatar>
															<Box
																sx={{
																	ml: 2
																}}
															>
																<Typography 
																	color="textPrimary"
																	sx={{
																		lineHeight: 1
																	}}
																>
																	<Typography 
																		variant="body2" 
																		color="textSecondary"
																		component="span"
																	>
																		{t("recipeDetails_info_costHeader")}
																	</Typography><br/>
																	<b>{t(translationsMaps.costs[data.cost])}</b>
																</Typography>
															</Box>
														</Stack>
													</Grid>
												}
												<Grid item xs={6}>
													<Stack
														direction="row"
														alignItems="center"
													>
														<Avatar 
															sx={{
																background: '#F5F1EA',
																width: '3.2rem',
																height: '3.2rem'
															}} 
															variant="rounded"
														>
															<PotIcon fontSize="large" />
														</Avatar>
														<Box
															sx={{
																ml: 2
															}}
														>
															<Typography 
																color="textPrimary"
																sx={{
																	lineHeight: 1
																}}
															>
																<Typography 
																	variant="body2" 
																	color="textSecondary"
																	component="span"
																>
																	{t("recipeDetails_info_cookingTimeHeader")}
																</Typography><br/>
																<b>{data.times.cooking ? readableTime(data.times.cooking, 'minutes') : t("recipeDetails_info_cookingEmptyStateHeader")}</b>
															</Typography>
														</Box>
													</Stack>
												</Grid>
											</Grid>
										</Box>
										<Box
											sx={{
												display: {
													xs: 'none',
													md: 'block'
												}
											}}
										>
											<Highlights 
												difficulty={data.difficulty}
												duration={data.times.total}
												rating={data.rating}
												hasRated={hasRated}
											/>
										</Box>
									</Paper>
									{
										!data.rating.user_rated &&
										<Paper
											sx={{
												mt: 3,
												p: 1,
												pl: 2,
												display: {
													xs: 'none',
													md: 'block'
												}
											}}
										>
											<Stack
												direction="row"
												alignItems="center"
												justifyContent="space-between"
											>
												<Typography
													color="yellow.main"
												>
													<b>{t("recipeDetails_rating_header")}</b>
												</Typography>
												<Rating 
													id={data.id}
													rating={data.rating} 
													onNewValue={(value) => setHasRated(true) }
												/>
											</Stack>
										</Paper>
									}
									<Stack
										direction={isMobile ? 'column-reverse' : 'column'}
									>
										<Box>
											{
												data.paragraphs.map((paragraph, i) => (
													<Paper
														key={'paragraph-'+i}
														sx={{
															mt: 3,
															p: 1
														}}
													>
														<Accordion
															defaultExpanded
															data={paragraph}
															bodyVariant="body2"
														/>
													</Paper>
												))
											}
										</Box>
										<Box>
											<Stack 
												direction="row"
												sx={{
													mt: 3
												}}
												alignItems="center"
											>
												<Chip component="h3" label={t("recipeDetails_ingredients_header")} color="primary" sx={{m:0}} />
												<Typography
													color="textPrimary"
													component="div"
													sx={{ml: 2}}
												>
													{data.ingredients.label}
												</Typography>
											</Stack>
											<Box>
												{
													data.ingredients.groups.map((group, i) => (
														<Box key={'group-'+i} my={4}>
															<Typography color="textPrimary" variant="body2" gutterBottom>
																{group.label}
															</Typography>
															{
																group.ingredients.map((ingredient, p) => (
																	<Box key={'ingredient-'+p} mt={2} pl={2}>
																		<Chip 
																			component={NavLink}
																			to={generatePath('/ingredienti/:slug', {slug: ingredient.slug})}
																			size="small" 
																			color="white" 
																			rel="nofollow"
																			label={
																				<Typography 
																					variant="body2" 
																					component="span" 
																					color="primary"
																				>
																					<b><u>{ ingredient.name }</u></b>&nbsp;&nbsp;&nbsp;
																					<Typography color="textPrimary" variant="body2" component="span">{ingredient.quantity}</Typography>
																				</Typography>
																			} 
																			clickable
																		/>
																	</Box>
																))
															}
														</Box>
													))
												}
											</Box>
										</Box>
									</Stack>
								</Grid>
							</Grid>		
						</Grid>
						<Grid 
							item 
							xs={12} 
							md={11} 
							xl={10} 
							sx={{mt: 10}}
						>
							<Typography
								color="text.primary"
								variant="h4"
								sx={{mb: 3}}
							>
								<b>{t("recipeDetails_relatedRecipes_header")}</b>
							</Typography>
							<Related id={data.id} />
						</Grid>
					</Grid>
				</>
			}
		</Layout>
	);
}

export default Recipe;